import client from "@/api/openapi-ts";

const VITE_API_URL = import.meta.env.VITE_API_URL;
const VITE_MODE = import.meta.env.VITE_MODE;

const FEATURE_FLAG_URL =
  VITE_MODE === "dev" ? "http://localhost:8000" : VITE_API_URL;

// A FeatureFlags class meant to be used as a singleton throughout the Platform UI.
// Its main function is `flagIsEnabled` which is used to check if a feature flag is enabled
// based on a dictionary of arguments. These args will be run through the flag rules defined
// in the Flags UI for the given flag.
// This singleton class contains a cache of flag values to avoid hitting the flag server many times
// if the flag is evaluated multiple times in a short period of time.
// The cached values are only valid for 30 seconds. The flag server refreshes flags every 60 seconds.
class FeatureFlags {
  static #instance = null;

  constructor() {
    // As an extra precaution, don't allow another instantiation of this class.
    // The class isn't exported, so that should be enough, but this can't hurt.
    if (FeatureFlags.#instance) {
      throw new Error(
        "FeatureFlags class is a singleton. Use the 'featureFlags' instance instead of creating a new instance.",
      );
    }

    FeatureFlags.#instance = this;
    this._cache = new Map();
    this._cacheTTL = 30 * 1000; // 30 seconds
  }

  _getCacheKey(flagName, flag_args) {
    return `${flagName}-${JSON.stringify(flag_args)}`;
  }

  _isCacheValid(cacheEntry) {
    return cacheEntry && Date.now() - cacheEntry.timestamp < this._cacheTTL;
  }

  async flagIsEnabled(flagName, flag_args = {}) {
    const cacheKey = this._getCacheKey(flagName, flag_args);
    const cachedValue = this._cache.get(cacheKey);

    if (this._isCacheValid(cachedValue)) {
      console.log("cache hit for flag", flagName, "with args", flag_args);
      console.log("cached value", cachedValue.value);
      return cachedValue.value;
    }

    try {
      // The feature-flags server is a separate service (and not part of the
      // platform_api OpenAPI schema), so override the shared client's baseUrl
      // for this request. Auth headers / 401 handling still come from the
      // client's middleware.
      const { data, response } = await client.POST(
        "/api/internal/v1/feature-flags-server/",
        {
          baseUrl: FEATURE_FLAG_URL,
          body: {
            feature_flag_name: flagName,
            feature_flag_args: flag_args,
          },
          signal: AbortSignal.timeout(5000),
        },
      );

      if (response.status !== 200) {
        console.error(`Flag evaluation error code: ${response.status}`);
        return false;
      }

      if (!data || data.eval === undefined) {
        console.error(`Flag evaluation error: 'eval' not found in response`);
        return false;
      }

      // Store in cache. Replaces any existing entry.
      this._cache.set(cacheKey, {
        value: data.eval,
        timestamp: Date.now(),
      });

      return data.eval;
    } catch (error) {
      console.error(`Flag evaluation failed: ${error.stack || error}`);
      return false;
    }
  }
}

// Export a singleton instance
export const featureFlags = new FeatureFlags();
